import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { useEffect, useId, useMemo, useRef, useState } from "react"
import { toastApiError } from "../../../api/apiError"
import {
  ARTICLE_RESOLVE_CHUNK,
  useBulkSetProductGroup,
  useProductGroups,
  useResolveArticles,
} from "../../../api/articlesApi"
import { useTemplateSettings } from "../../../api/templateSettingsApi"
import { SectionLabel } from "../../shared/SectionLabel"
import { ArticleProductGroupCombobox } from "./ArticleProductGroupCombobox"

interface ArticleProductGroupAssignSectionProps {
  /** The checkbox-selected article ids from the sibling list panel (TUR-1539
   * D1) — assignment always targets exactly this set, never a filter
   * combination, so there is no separate "affected count" preview here:
   * `articleIds.length` already IS the count. */
  articleIds: string[]
}

/**
 * Inline replacement for the removed standalone "assign product group"
 * button + dialog: bulk-assigns product_group to exactly the
 * checkbox-selected articles. Rendered inside SendInquiryPanel, below the
 * recipients, as the "more actions" section: the inquiry stays the primary
 * purpose of a selection.
 */
export function ArticleProductGroupAssignSection({
  articleIds,
}: ArticleProductGroupAssignSectionProps) {
  const { translate } = useTranslation()
  const headingId = useId()
  const [value, setValue] = useState("")
  const { data: templateSettings } = useTemplateSettings()
  const { data: productGroups } = useProductGroups()
  const bulkSet = useBulkSetProductGroup()
  const label =
    templateSettings?.productGroupLabel ||
    translate("MC_MD_FIELD_PRODUCT_GROUP")

  // Resolve exactly the selected ids, instead of reading whatever pages a
  // sibling list query happened to have loaded. A selection past the resolve
  // cap is not worth a prefill (nobody reviews 1000 shared values before
  // typing one), so it just stays unprefilled rather than costing a request
  // per chunk.
  const canPrefill = articleIds.length <= ARTICLE_RESOLVE_CHUNK
  const { data: selectedRows } = useResolveArticles(articleIds, canPrefill)

  /** The group every selected article already carries, or null when they
   *  differ, when one has none, or while the rows are still loading: a
   *  partial set could agree by accident. */
  const sharedGroup = useMemo(() => {
    const selected = selectedRows ?? []
    if (selected.length === 0 || selected.length !== articleIds.length) {
      return null
    }
    const first = selected[0].productGroup
    if (!first) return null
    return selected.every((r) => r.productGroup === first) ? first : null
  }, [selectedRows, articleIds])

  // The field belongs to ONE selection, so its identity is the sorted id set
  // (article ids are uuids, so a comma join cannot collide).
  const selectionKey = useMemo(
    () => [...articleIds].sort().join(","),
    [articleIds],
  )

  // Changing the checkbox set re-arms the prefill AND drops what the previous
  // selection left behind: a group read off articles A and B must not stay in
  // the box and get assigned to C and D.
  const prefilledFor = useRef<string | null>(null)
  useEffect(() => {
    prefilledFor.current = null
    setValue("")
  }, [selectionKey])

  // Prefill once per selection, on the transition to a fully resolved one: the
  // rows arrive after the first render, and re-applying on every change would
  // overwrite what the user is typing.
  useEffect(() => {
    if (prefilledFor.current === selectionKey || sharedGroup == null) return
    prefilledFor.current = selectionKey
    setValue(sharedGroup)
  }, [selectionKey, sharedGroup])

  function handleAssign() {
    const productGroup = value.trim()
    if (!productGroup) return
    bulkSet.mutate(
      { filters: { articleIds }, productGroup },
      {
        onSuccess: (count) => {
          toast.success(
            translate("MC_MD_BULK_PRODUCT_GROUP_SUCCESS", { label, count }),
          )
          setValue("")
        },
        onError: (err) => toastApiError(err, translate, "MC_MD_SAVE_ERROR"),
      },
    )
  }

  return (
    <div className="mt-5 flex flex-col gap-2">
      <SectionLabel>{translate("MC_MD_BULK_MORE_ACTIONS")}</SectionLabel>
      <div className="flex flex-col gap-2 rounded-lg border border-grey4 bg-grey2 p-4">
        <span id={headingId} className="text-sm font-semibold text-white">
          {translate("MC_MD_BULK_PRODUCT_GROUP_TITLE_FOR", {
            label,
            n: articleIds.length,
          })}
        </span>
        <div className="flex items-start gap-2">
          <ArticleProductGroupCombobox
            value={value}
            onChange={setValue}
            aria-labelledby={headingId}
            options={productGroups ?? []}
            createLabel={(text) =>
              translate("MC_MD_BULK_PRODUCT_GROUP_CREATE_NEW", { label, text })
            }
            placeholder={translate(
              "MC_MD_BULK_PRODUCT_GROUP_INPUT_PLACEHOLDER",
              { label },
            )}
            className="h-9 border-grey4 bg-grey1 text-sm text-white"
          />
          <Button
            onClick={handleAssign}
            disabled={bulkSet.isPending || !value.trim()}
          >
            {bulkSet.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {translate("MC_MD_BULK_PRODUCT_GROUP_ASSIGN")}
          </Button>
        </div>
      </div>
    </div>
  )
}
